import React from "react";
import * as analytics from "../../Util/Analytics/TableAnalytics";
import { isSpecialCode, parseNumber, isCensusSite } from "../../Util/SuppressionRules";

const SubQuestionTableRow = ({
  allLocations,
  comparisonData,
  isPatternsOverTime,
  linkFirstColumn,
  onSubQuestionChange,
  onLocationChange,
  printRange,
  showSampleSize,
  subQuestionData,
}) => {
  // Helper to detect suppression code present on any relevant field
  const hasSuppressionCode = (data) => {
    const fields = [data?.NPercent, data?.DataValue, data?.DataValueRaw, data?.Percent];
    return fields.some((v) => isSpecialCode(v));
  };

  // Helper function to format percentage display
  const formatPercent = (data) => {
    const value = data?.NPercent ?? data?.DataValue ?? data?.DataValueRaw ?? data?.Percent;

    // Suppression codes take priority across any field: show em-dash
    if (hasSuppressionCode(data)) return "—";

    // Missing data across all fields: show em-dash
    if (value === null || value === undefined || value === '') return "—";
    
    // Parse numeric value
    const numVal = parseNumber(value);
    if (numVal === null || isNaN(numVal)) return "—";
    
    // 0 or 100 (when not suppressed): show without decimals
    if (numVal === 0 || numVal === 100) return String(Math.round(numVal));
    
    // Valid data: show with 1 decimal place
    return numVal.toFixed(1);
  };

  // Helper function to format CI display
  const formatCI = (data) => {
    const value = data?.NPercent ?? data?.DataValue ?? data?.DataValueRaw ?? data?.Percent;
    const ci = data?.ConfidenceInterval;

    // Suppression codes take priority across any field: show em-dash
    if (hasSuppressionCode(data)) return "—";
    // Census sites: CI not calculated
    if (isCensusSite(data)) return "N/A";

    // Missing data across all fields: show em-dash
    if (value === null || value === undefined || value === '') return "—";
    
    // Parse numeric value to check for NaN
    const numVal = parseNumber(value);
    if (numVal === null || isNaN(numVal)) return "—";
    
    // 0 or 100 should display without CI
    if (numVal === 0 || numVal === 100) return "N/A";

    // Treat common dash placeholders as missing CI
    const ciStr = ci !== undefined && ci !== null ? String(ci).trim() : "";
    const ciIsDash = ciStr === "—" || ciStr === "-" || ciStr === "--" || ciStr === "–";

    // If CI is explicitly "N/A", return it
    if (ciStr.toUpperCase() === "N/A" || ciStr.toUpperCase() === "NA") {
      return "N/A";
    }

    // If CI is missing, a dash placeholder, or a suppression code, treat as missing
    if (!ci || ciIsDash || isSpecialCode(ci)) return "—";
    
    const parts = String(ci).split('-').map(p => p.trim());
    if (parts.length === 2) {
      const low = parseNumber(parts[0]);
      const high = parseNumber(parts[1]);
      if (low !== null && high !== null && !isNaN(low) && !isNaN(high)) {
        return ci;
      }
    }

    // Non-numeric CI string: treat as missing
    return "—";
  };

  // Helper function to format Range string (e.g., "19.6 - 100.0" -> "19.6 - 100")
  const formatRange = (rangeString) => {
    if (!rangeString) return "—";
    
    const parts = rangeString.split('-').map(p => p.trim());
    if (parts.length !== 2) return rangeString;
    
    const formatNum = (numStr) => {
      const num = parseNumber(numStr);
      if (num === null || isNaN(num)) return numStr;
      // Show 0 and 100 without decimals
      if (num === 0 || num === 100) return String(Math.round(num));
      // Otherwise show 1 decimal place
      return num.toFixed(1);
    };
    
    return `${formatNum(parts[0])} - ${formatNum(parts[1])}`;
  };

  const formatSampleSize = (data, fallback) => {
    if (fallback === null || fallback === undefined || fallback === "") return "—";
    return fallback;
  };
  const rowText = GetSubRowText();
  function GetSubRowText() {
    // In Subquestion Over Time view, always display the Year as the
    // primary label in the first column.
    if (isPatternsOverTime) {
      return subQuestionData.Yr;
    }
    if (allLocations) {
      return subQuestionData.LocationTxt;
    }
    if (subQuestionData && subQuestionData.SubQuestion !== "") {
      return subQuestionData.SubQuestion;
    }
    return "";
  }

  return (
    <>
      {printRange && subQuestionData.length > 0 ? (
        <tr>
          <td style={{ textAlign: "left", borderRight: "none" }}>
            <b>Range</b>
          </td>
          {/* Align columns: optional N column, then % and CI */}
          {showSampleSize && <td style={{}}></td>}
          <td colSpan="1" style={{}}>
            <strong>{formatRange(subQuestionData[0].Range)}</strong>
          </td>
          <td style={{}}></td>
          {/* If comparison is shown, include its optional N placeholder and cells */}
          {comparisonData && (
            <>
              {showSampleSize && <td style={{}}></td>}
              <td style={{}}></td>
              <td style={{}}></td>
            </>
          )}
        </tr>
      ) : !printRange ? (
        <>
          <tr
            id={
              subQuestionData.LocationAlias &&
              subQuestionData.LocationAlias === "National"
                ? `US`
                : ""
            }
          >
            <th className="th-secondary" style={{ textAlign: "left" }}>
              {linkFirstColumn && !isPatternsOverTime ? (
                <button
                  className="btn btn-link m-0 p-0 text-left"
                  onClick={(e) => {
                    e.preventDefault();
                    if (allLocations) {
                      const locationId =
                        subQuestionData.LocationAbbr ||
                        subQuestionData.LocationID ||
                        subQuestionData.LocationId;
                      analytics.selectionTableLocation({
                        locationAbbr: subQuestionData.LocationAbbr,
                        locationId,
                      });
                      onLocationChange(
                        {
                          LocationAbbr: subQuestionData.LocationAbbr,
                          LocationDesc: subQuestionData.LocationTxt,
                          LocationType: subQuestionData.LocationType,
                        },
                        "table"
                      );
                    } else if (typeof onSubQuestionChange === 'function') {
                      analytics.selectTableSubQuestion({
                        questionId: subQuestionData.QuestionId,
                        subQuestionId: subQuestionData.SubQuestionId,
                      });
                      onSubQuestionChange(subQuestionData.SubQuestionId);
                    }
                  }}
                >
                  {rowText}
                </button>
              ) : (
                <>{rowText}</>
              )}
            </th>
            {/* Optional Sample Size as second column */}
            {showSampleSize && (
              <td>{formatSampleSize(subQuestionData, subQuestionData && subQuestionData.SampleSize)}</td>
            )}
            {/* Percent and CI always shown */}
            <td>{formatPercent(subQuestionData)}</td>
            <td>{(() => { const ci = formatCI(subQuestionData); return ci === null ? "N/A" : ci; })()}</td>
            {comparisonData && (
              <>
                {showSampleSize && (
                  <td>{formatSampleSize(comparisonData[0], comparisonData[0] && comparisonData[0].SampleSize)}</td>
                )}
                <td>{formatPercent(comparisonData[0])}</td>
                <td>{(() => { const ci = formatCI(comparisonData[0]); return ci === null ? "N/A" : ci; })()}</td>
              </>
            )}
          </tr>
        </>
      ) : (
        <></>
      )}
    </>
  );
};

export default SubQuestionTableRow;
